// Quick "connect with a member" modal. Opens from Jarvis member search results
// (and anywhere a member needs an immediate action) so the user can message or
// request a meeting without leaving the page.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  MessageSquare,
  CalendarPlus,
  Eye,
  Send,
  Loader2,
  CheckCircle2,
  User as UserIcon,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { messagingAPI } from '../api/client';
import { publicRoleLabel } from '../utils/roles';
import RequestMeetingModal from './RequestMeetingModal';
import MicButton from './MicButton';

export interface ConnectMember {
  id: number;
  full_name: string;
  avatar_url?: string;
  role?: string;
  chapter?: string;
}

interface ConnectMemberModalProps {
  member: ConnectMember;
  onClose: () => void;
}

export default function ConnectMemberModal({ member, onClose }: ConnectMemberModalProps) {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'choose' | 'message'>('choose');
  const [showMeeting, setShowMeeting] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const name = member.full_name || 'this member';
  const firstName = name.split(' ')[0];

  const handleSend = async () => {
    if (!messageText.trim() || sending) return;
    setSending(true);
    try {
      await messagingAPI.quickSend(member.id, messageText.trim());
      setSent(true);
      toast.success(`Message sent to ${name}.`);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string; detail?: string } } })?.response?.data
          ?.error ||
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Could not send your message. Please try again.';
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  // The meeting composer is a full modal of its own; show it in place of this one.
  if (showMeeting) {
    return (
      <RequestMeetingModal
        inviteeId={member.id}
        inviteeName={name}
        onClose={() => {
          setShowMeeting(false);
          onClose();
        }}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-navy">Connect with {name}</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100 text-gray-500"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Member summary */}
        <div className="px-6 py-4 flex items-center gap-3 border-b border-gray-100">
          <div className="w-12 h-12 bg-off-white rounded-full flex items-center justify-center flex-shrink-0">
            {member.avatar_url ? (
              <img
                src={member.avatar_url}
                alt={name}
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <UserIcon className="text-navy" size={22} />
            )}
          </div>
          <div className="min-w-0">
            <p className="font-medium text-navy truncate">{name}</p>
            <p className="text-sm text-gray-500 truncate">
              {[publicRoleLabel(member.role), member.chapter].filter(Boolean).join(' • ')}
            </p>
          </div>
        </div>

        {/* Choose an action */}
        {mode === 'choose' && (
          <div className="px-6 py-5 space-y-3">
            <button
              onClick={() => setMode('message')}
              className="w-full flex items-center gap-3 px-4 py-3 bg-navy text-white rounded-lg hover:bg-navy-dark transition"
            >
              <MessageSquare size={18} />
              Send a message
            </button>
            <button
              onClick={() => setShowMeeting(true)}
              className="w-full flex items-center gap-3 px-4 py-3 border border-navy text-navy rounded-lg hover:bg-off-white transition"
            >
              <CalendarPlus size={18} />
              Request a meeting
            </button>
            <button
              onClick={() => {
                onClose();
                navigate(`/members/${member.id}`);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-gray-600 hover:text-navy transition"
            >
              <Eye size={18} />
              View full profile
            </button>
          </div>
        )}

        {/* Inline message composer */}
        {mode === 'message' &&
          (sent ? (
            <div className="px-6 py-6 text-center space-y-4">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="text-green-600" size={28} />
              </div>
              <p className="text-gray-700">
                Your message to <span className="font-medium text-navy">{name}</span> has been sent.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  Done
                </button>
                <button
                  onClick={() => {
                    onClose();
                    navigate('/messages');
                  }}
                  className="flex-1 px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy-dark transition"
                >
                  Open conversation
                </button>
              </div>
            </div>
          ) : (
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message to {firstName}
                </label>
                <div className="relative">
                  <textarea
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    rows={4}
                    autoFocus
                    placeholder={`Hi ${firstName}, I'd love to connect...`}
                    className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent resize-none"
                  />
                  <MicButton
                    value={messageText}
                    onChange={setMessageText}
                    className="absolute bottom-2 right-2"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between gap-2">
                <button
                  onClick={() => setMode('choose')}
                  className="px-4 py-2 text-gray-600 hover:text-navy transition"
                >
                  Back
                </button>
                <button
                  onClick={handleSend}
                  disabled={!messageText.trim() || sending}
                  className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy-dark transition disabled:opacity-50"
                >
                  {sending ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Send
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
